import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { devWarn, devError } from 'utils/devLog';

const AdminContext = createContext();

const MEDUSA_BACKEND_URL = process.env.REACT_APP_MEDUSA_BACKEND_URL || 'http://localhost:9000';

export function AdminProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Identidad: paleta centralizada para el admin (Birch / Azure)
  const brand = {
    name: 'Birch/Azure',
    palette: { birch: '#FBF2EF', azure: '#4C637C' }
  };

  // Logout estable (usado por verifyToken) — declarado antes para evitar dependencias inestables
  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
  }, []);

  // Verifica el token antes de cualquier useEffect que lo invoque
  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${MEDUSA_BACKEND_URL}/admin/users/me`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token.trim()}` } : {},
      });

      // Sólo expulsar (logout) si el token es claramente inválido/expirado
      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Defensa en profundidad: /admin/users/me sólo responde 200 a tokens de admin,
        // pero exigimos forma esperada (id+email) por si algún día un token con scope
        // distinto se filtrara al campo admin_token. Cualquier respuesta sin esa forma
        // se trata como token inválido.
        const candidate = data.user || data;
        if (!candidate || !candidate.id || !candidate.email) {
          devWarn('verifyToken: respuesta sin forma de admin user, invalidando token');
          logout();
          return;
        }
        setAdmin(candidate);
      } else {
        // No eliminar token por errores de servidor (5xx) o respuestas inesperadas
        devWarn('verifyToken: respuesta inesperada', response.status);
      }
    } catch (error) {
      // Error de red: no invalidar token (entorno seguro). Mantener token para reintentos.
      devError('Error verifying token (network):', error);
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  const login = async (email, password, totp) => {
    try {
      const response = await fetch(`${MEDUSA_BACKEND_URL}/auth/user/emailpass`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(totp ? { email, password, totp } : { email, password }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 429 || err.code === 'totp_required') {
          throw new Error(err.message || 'Credenciales inválidas');
        }
        throw new Error('Credenciales inválidas');
      }

      const data = await response.json();
      const newToken = (data.token || data.access_token || '').trim();
      if (!newToken) throw new Error('No se recibió token');

      // Confirmar que la cuenta es de administrador ANTES de entrar; si no, el
      // panel abría un segundo y verifyToken expulsaba sin explicar por qué.
      const me = await fetch(`${MEDUSA_BACKEND_URL}/admin/users/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${newToken}` },
      });
      if (me.status === 401 || me.status === 403) {
        throw new Error('La contraseña es correcta, pero esta cuenta no está enlazada a un usuario administrador.');
      }

      localStorage.setItem('admin_token', newToken);
      setToken(newToken);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return (
    <AdminContext.Provider value={{ 
      token, 
      admin, 
      loading, 
      login, 
      logout,
      isAuthenticated: !!token,
      brand
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}
